import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Button, Divider, Card, Slider, Select, Input, Modal, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import AddConditionModal from './AddConditionModal';
import { addMatchRuleByEntityId, updateMatchRuleByEntityId } from "../../../../api's/DataModelApi";
import LoadingIcon from '../../../../reusable-component/LoadingIcon';

const { Text } = Typography;

const MatchandMergeDetails = ({ entityId, attributes, handleClose, editingData }) => {
  const [mergeThreshold, setMergeThreshold] = useState(85);
  const [conditionList, setConditionList] = useState([]);

  const [ruleStrategy, setRuleStrategy] = useState('auto_merge');
  const [matchRuleName, setMatchRuleName] = useState('');
  const [matchRuleDescription, setMatchRuleDescription] = useState('');

  const [openConditionModal, setOpenConditionModal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Prefill when editing
  useEffect(() => {
    if (editingData) {
      setMatchRuleName(editingData.matchRuleName || '');
      setMatchRuleDescription(editingData.matchRuleDescription || '');
      setRuleStrategy(editingData.ruleStrategy || 'auto_merge');
      setMergeThreshold(editingData.ruleOverallThreshold || 50);
      setConditionList(editingData.ruleCondition || []);
    } else {
      setMatchRuleName('');
      setMatchRuleDescription('');
      setRuleStrategy('auto_merge');
      setMergeThreshold(85);
      setConditionList([]);
    }
  }, [editingData]);

  async function handleSubmit() {
    setLoading(true);

    const payload = {
      matchRuleName,
      matchRuleDescription,
      ruleStrategy,
      ruleOverallThreshold: mergeThreshold,
      ruleCondition: conditionList,
    };

    try {
      let response;

      if (editingData) {
        response = await updateMatchRuleByEntityId(editingData.id, payload);
      } else {
        response = await addMatchRuleByEntityId(entityId, payload);
      }

      if (response.status === 200) {
        enqueueSnackbar(editingData ? 'Match rule updated!' : 'Match rule saved!', { variant: 'success' });
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar('Failed to save rule', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: 5 }}>
      {/* Strategy + Threshold */}
      <h3> {editingData ? 'Update' : 'Create'} Rule</h3>
      <div
        style={{
          marginBottom: 20,
          marginTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Strategy */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text>Strategy:</Text>
          <Select
            style={{ width: 160 }}
            value={ruleStrategy}
            onChange={(val) => setRuleStrategy(val)}
            options={[
              { label: 'Potential Merge', value: 'potential_merge' },
              { label: 'Auto Merge', value: 'auto_merge' },
            ]}
          />
        </div>

        {/* Merge Threshold */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Text style={{ whiteSpace: 'nowrap' }}>Merge ≥</Text>

          <Slider
            min={0}
            max={100}
            value={mergeThreshold}
            onChange={(value) => setMergeThreshold(value)}
            style={{ width: 200 }}
          />

          <Input
            style={{ width: 70 }}
            value={mergeThreshold}
            onChange={(e) => {
              let val = e.target.value;

              if (val === '') {
                setMergeThreshold('');
                return;
              }

              val = Number(val);
              if (!Number.isNaN(val)) {
                if (val < 0) val = 0;
                if (val > 100) val = 100;
                setMergeThreshold(val);
              }
            }}
            onBlur={() => {
              if (mergeThreshold === '') setMergeThreshold(0);
            }}
            maxLength={3}
          />
        </div>
      </div>

      {/* Rule Name + Description */}
      <Row gutter={16}>
        <Col span={8}>
          <Form layout="vertical">
            <Form.Item label="Match Rule Name:">
              <Input
                placeholder="e.g. email rule"
                value={matchRuleName}
                onChange={(e) => setMatchRuleName(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col span={16}>
          <Form layout="vertical">
            <Form.Item label="Description:">
              <Input
                placeholder="description"
                value={matchRuleDescription}
                onChange={(e) => setMatchRuleDescription(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Col>

        {/* Conditions */}
        <Col span={24} style={{ marginTop: 20 }}>
          <Card>
            <Typography.Title level={5}>Rule Conditions</Typography.Title>
            <Divider />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {conditionList.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    padding: 10,
                  }}
                >
                  <Text>{item.attributeName}</Text>
                  <Text>{item.matchType} match</Text>

                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedCondition(item);
                      setEditingIndex(idx);
                      setOpenConditionModal(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              style={{ marginTop: 16 }}
              onClick={() => {
                setSelectedCondition(null);
                setEditingIndex(null);
                setOpenConditionModal(true);
              }}
            >
              Add Condition
            </Button>

            <AddConditionModal
              attributes={attributes}
              conditionList={conditionList}
              setConditionList={setConditionList}
              open={openConditionModal}
              onClose={() => setOpenConditionModal(false)}
              editingIndex={editingIndex}
              selectedCondition={selectedCondition}
            />
          </Card>
        </Col>

        {/* Footer */}
        <Col
          span={24}
          style={{
            marginTop: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit}>
            {editingData ? 'Update Rule' : 'Save Rule'}
          </Button>
        </Col>
      </Row>

      {loading && <LoadingIcon />}
    </div>
  );
};

export default MatchandMergeDetails;
